var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGetSpaceByIdForDraftEventMeetingServicePermissionQuery } from '../../generated/graphql';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useSafeFormData, ComposerMode, ComposerAnalyticsEvent, } from '../types/index';
import { defaultUseGetLocationLevelReturn, useGetLocationLevel, } from '../hooks/useLocationLevel/useGetLocationLevel';
import { emptySpacesOrchestrationState, getManagedSpace, isOrganizerSpace, normalizeSpaces, violatesRequestOnlyLock, } from '../utils/spaceOrchestration';
import { useConfig } from '../context/ConfigContext';
import { useApprovalPermission } from '../hooks/useApprovalPermission';
var defaultEventComposerGlobalContextValue = __assign({ timezone: '', timeFormat: 'h:mm a', requestOnlySpace: undefined, requestFormId: undefined, spaceRequestFormCompletion: 'unknown', setSpaceRequestFormCompletion: function () { }, hasAttemptedSubmit: false, setHasAttemptedSubmit: function () { }, composerMode: ComposerMode.CREATE, backgrounded: false, onError: function () { }, onTrackBufferEvent: undefined, onTrackComposerEvent: undefined, setSpaceBookingOpen: function () { }, spacesState: emptySpacesOrchestrationState, initialSpaces: undefined, setPendingSpaceSwap: function () { }, setSpaces: function () { }, removePendingSpace: function () { }, removeSpace: function () { }, spacesWithConflicts: null, isCheckingConflicts: false, updateSpaceConflicts: function () { }, clearSpaceConflict: function () { }, incrementConflictChecks: function () { }, decrementConflictChecks: function () { }, canApprove: false, approvalPermissionLoading: false, hasSpacesReservePublicPermissionForCurrentTenant: false, permissionsLoading: false }, defaultUseGetLocationLevelReturn);
var EventComposerGlobalContext = createContext(defaultEventComposerGlobalContextValue);
export var EventComposerGlobalProvider = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var children = _a.children, composerMode = _a.composerMode, timezone = _a.timezone, timeFormat = _a.timeFormat, onError = _a.onError, setSpaceBookingOpen = _a.setSpaceBookingOpen, onTrackBufferEvent = _a.onTrackBufferEvent, onTrackComposerEvent = _a.onTrackComposerEvent, defaultLevelId = _a.defaultLevelId, _h = _a.backgrounded, backgrounded = _h === void 0 ? false : _h, initialSpaces = _a.initialSpaces;
    var userPermissions = useUserPermissions();
    var setValue = useFormContext().setValue;
    var getValues = useFormContext().getValues;
    var formData = useSafeFormData();
    /* Source of truth for location and level */
    var _j = useGetLocationLevel(defaultLevelId), locationId = _j.locationId, levelId = _j.levelId, setLocationId = _j.setLocationId, setLevelId = _j.setLevelId, locationLevelLoading = _j.loading;
    var _k = __read(useState(emptySpacesOrchestrationState), 2), spacesState = _k[0], setSpacesState = _k[1];
    var setPendingSpaceSwap = useCallback(function (pendingSpaceSwap) {
        return setSpacesState(function (state) { return (__assign(__assign({}, state), { pendingSpaceSwap: pendingSpaceSwap })); });
    }, []);
    // When the host backgrounds the composer (e.g. the office space-preview
    // flow takes the sider slot), close the add-space drawer — it portals to
    // document.body, so it would float over the sider that replaced us. A
    // pending "change space" swap is abandoned with it, mirroring the drawer's
    // own close handler (see SpaceBookingInternal). Drawers owned by deeper
    // components (suggested times) subscribe to `backgrounded` off this
    // context and close themselves the same way.
    useEffect(function () {
        if (backgrounded) {
            setSpaceBookingOpen(false);
            setPendingSpaceSwap(null);
        }
    }, [backgrounded, setSpaceBookingOpen, setPendingSpaceSwap]);
    var _l = __read(useState(null), 2), spacesWithConflicts = _l[0], setSpacesWithConflicts = _l[1];
    // TODO: This feels like a hack for referential stability of the isCheckingConflicts value.
    // We should find a better way to handle the usesSpaceConflictDetection hook input values
    // Tracks how many space conflict checks are currently in-flight. We use a
    // counter rather than a boolean because multiple spaces check in parallel —
    // flipping a boolean to false when the first space finishes would incorrectly
    // unblock submission while others are still pending. The counter reaches zero
    // only when every in-flight check has completed.
    var _m = __read(useState(0), 2), pendingConflictChecks = _m[0], setPendingConflictChecks = _m[1];
    var incrementConflictChecks = useCallback(function () { return setPendingConflictChecks(function (count) { return count + 1; }); }, []);
    var decrementConflictChecks = useCallback(
    // Math.max guards against going negative if a decrement fires without a
    // matching increment (e.g. an error thrown before the check started).
    function () { return setPendingConflictChecks(function (count) { return Math.max(0, count - 1); }); }, []);
    var updateSpaceConflicts = useCallback(function (spaceId, conflict) {
        var hasConflict = conflict.spaceBuffers.before ||
            conflict.spaceBuffers.after ||
            conflict.eventTime;
        setSpacesWithConflicts(function (prev) {
            var _a;
            if (hasConflict) {
                return __assign(__assign({}, (prev !== null && prev !== void 0 ? prev : {})), (_a = {}, _a[spaceId] = conflict, _a));
            }
            if (!prev || !(spaceId in prev)) {
                return prev;
            }
            var _b = prev, _c = spaceId, _removed = _b[_c], rest = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
            return Object.keys(rest).length === 0 ? null : rest;
        });
    }, []);
    var clearSpaceConflict = useCallback(function (spaceId) {
        setSpacesWithConflicts(function (prev) {
            if (!prev || !(spaceId in prev)) {
                return prev;
            }
            var _a = prev, _b = spaceId, _removed = _a[_b], rest = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
            return Object.keys(rest).length === 0 ? null : rest;
        });
    }, []);
    // The wrapped write for form.spaces: normalize, clear conflict state for
    // spaces the normalizer dropped, then write. See spaceOrchestration.ts.
    var setSpaces = useCallback(function (spaces, options) {
        var _a;
        if (options === void 0) { options = {}; }
        // A persisted request-only event's space can't be swapped, replaced,
        // or removed — by anyone, before or after approval. Reject the write;
        // covers writers with no UI gate (cross-app patches).
        if (violatesRequestOnlyLock(initialSpaces, spaces))
            return;
        var _b = normalizeSpaces({
            spaces: spaces,
            initialSpaces: initialSpaces,
        }), normalized = _b.spaces, droppedSpaceIds = _b.droppedSpaceIds;
        droppedSpaceIds.forEach(clearSpaceConflict);
        setValue('spaces', normalized, {
            shouldDirty: (_a = options.shouldDirty) !== null && _a !== void 0 ? _a : true,
        });
    }, [initialSpaces, clearSpaceConflict, setValue]);
    var removeSpace = useCallback(function (spaceId, viaSwap) {
        if (viaSwap === void 0) { viaSwap = false; }
        // A persisted request-only space can never be removed or swapped out
        // (setSpaces would reject the write anyway, but returning here keeps
        // conflict state and the analytics event honest).
        var managedSpace = getManagedSpace(initialSpaces);
        if ((managedSpace === null || managedSpace === void 0 ? void 0 : managedSpace.id) === spaceId)
            return;
        // The organizer space can never be deliberately removed (that would
        // cancel the event). The UI already blocks this; the guard covers
        // programmatic callers.
        if (!viaSwap && isOrganizerSpace(initialSpaces, spaceId))
            return;
        clearSpaceConflict(spaceId);
        var currentSpaces = getValues('spaces');
        if (!(currentSpaces === null || currentSpaces === void 0 ? void 0 : currentSpaces.length))
            return;
        setSpaces(currentSpaces.filter(function (space) { return space.id !== spaceId; }));
        // `viaSwap` marks removals that are half of a "change space" swap so
        // deliberate removals can be counted separately in Amplitude.
        onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.REMOVED_SPACE, {
            spaceId: spaceId,
            mode: composerMode,
            viaSwap: viaSwap,
        });
    }, [
        setSpaces,
        getValues,
        clearSpaceConflict,
        onTrackComposerEvent,
        composerMode,
        initialSpaces,
    ]);
    var removePendingSpace = useCallback(function (pendingSpaceSwap) {
        var spaceId = pendingSpaceSwap.spaceId;
        if (!spaceId)
            return;
        removeSpace(spaceId, true);
        setPendingSpaceSwap(null);
    }, [removeSpace, setPendingSpaceSwap]);
    /* This assume the event being created/edited is only for a single space and that space is managed(request only) */
    var requestOnlySpace = (_b = getManagedSpace(formData === null || formData === void 0 ? void 0 : formData.spaces)) !== null && _b !== void 0 ? _b : undefined;
    var _o = useConfig(), userContext = _o.userContext, tenantId = _o.tenantId;
    var orgIdForApproval = (_d = (_c = userContext.currentOrg) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : tenantId;
    var _p = useApprovalPermission({
        orgId: orgIdForApproval,
        managedSpaceId: composerMode === ComposerMode.EDIT ? requestOnlySpace === null || requestOnlySpace === void 0 ? void 0 : requestOnlySpace.id : undefined,
    }), canApprove = _p.canApprove, approvalPermissionLoading = _p.loading;
    var spaceRequestFormData = 
    /* @TODO this name is misleading, this just gets the space */
    useGetSpaceByIdForDraftEventMeetingServicePermissionQuery({
        variables: { spaceId: ((_e = requestOnlySpace === null || requestOnlySpace === void 0 ? void 0 : requestOnlySpace.id) === null || _e === void 0 ? void 0 : _e.toString()) || '' },
        skip: !(requestOnlySpace === null || requestOnlySpace === void 0 ? void 0 : requestOnlySpace.id),
    }).data;
    // Guard on requestOnlySpace: Apollo retains the last data after `skip`
    // flips true, so without it a removed request-only space would leave a
    // stale requestFormId behind (and validation keys off requestFormId).
    var requestFormId = requestOnlySpace
        ? (_g = (_f = spaceRequestFormData === null || spaceRequestFormData === void 0 ? void 0 : spaceRequestFormData.getSpaceById) === null || _f === void 0 ? void 0 : _f.requestForm) === null || _g === void 0 ? void 0 : _g.id
        : undefined;
    var _q = __read(useState('unknown'), 2), spaceRequestFormCompletion = _q[0], setSpaceRequestFormCompletion = _q[1];
    var _r = __read(useState(false), 2), hasAttemptedSubmit = _r[0], setHasAttemptedSubmit = _r[1];
    var contextValue = useMemo(function () { return (__assign(__assign({}, userPermissions), { removeSpace: removeSpace, removePendingSpace: removePendingSpace, userPermissions: userPermissions, timezone: timezone, timeFormat: timeFormat, requestOnlySpace: requestOnlySpace, requestFormId: requestFormId, spaceRequestFormCompletion: spaceRequestFormCompletion, setSpaceRequestFormCompletion: setSpaceRequestFormCompletion, hasAttemptedSubmit: hasAttemptedSubmit, setHasAttemptedSubmit: setHasAttemptedSubmit, composerMode: composerMode, backgrounded: backgrounded, locationId: locationId, levelId: levelId, setLocationId: setLocationId, setLevelId: setLevelId, loading: locationLevelLoading, onError: onError, onTrackBufferEvent: onTrackBufferEvent, onTrackComposerEvent: onTrackComposerEvent, setSpaceBookingOpen: setSpaceBookingOpen, spacesState: spacesState, initialSpaces: initialSpaces, setPendingSpaceSwap: setPendingSpaceSwap, setSpaces: setSpaces, spacesWithConflicts: spacesWithConflicts, isCheckingConflicts: pendingConflictChecks > 0, updateSpaceConflicts: updateSpaceConflicts, clearSpaceConflict: clearSpaceConflict, incrementConflictChecks: incrementConflictChecks, decrementConflictChecks: decrementConflictChecks, canApprove: canApprove, approvalPermissionLoading: approvalPermissionLoading })); }, [
        removeSpace,
        removePendingSpace,
        userPermissions,
        timezone,
        timeFormat,
        requestOnlySpace,
        requestFormId,
        spaceRequestFormCompletion,
        hasAttemptedSubmit,
        composerMode,
        backgrounded,
        locationId,
        levelId,
        setLocationId,
        setLevelId,
        locationLevelLoading,
        onError,
        onTrackBufferEvent,
        onTrackComposerEvent,
        setSpaceBookingOpen,
        spacesState,
        initialSpaces,
        setPendingSpaceSwap,
        setSpaces,
        spacesWithConflicts,
        pendingConflictChecks,
        updateSpaceConflicts,
        clearSpaceConflict,
        incrementConflictChecks,
        decrementConflictChecks,
        canApprove,
        approvalPermissionLoading,
    ]);
    return (_jsx(EventComposerGlobalContext.Provider, { value: contextValue, children: children }));
};
export var useEventComposerGlobalContext = function () {
    return useContext(EventComposerGlobalContext);
};
